import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from '@/hooks/use-toast';
import { useUserRoles } from './useUserRoles';
import { useLanguage } from '@/contexts/LanguageContext';

export interface Property {
  id: string;
  user_id: string;
  image: string;
  images?: string[];
  primary_image_index?: number;
  price: number;
  address: string;
  city: string;
  rooms: number;
  size: number;
  plot_size: number;
  type: string;
  monthly_fee?: number;
  description?: string;
  features?: string[];
  latitude?: number;
  longitude?: number;
  sold: boolean;
  sold_date?: string;
  sold_price?: number;
  is_boosted?: boolean;
  boost_expires_at?: string;
  boost_count?: number;
  video_url?: string;
  is_nyproduktion?: boolean;
  approval_status?: string;
  // Rental fields
  listing_type?: string;
  rental_period?: string;
  deposit_amount?: number;
  is_furnished?: boolean;
  utilities_included?: boolean;
  minimum_lease_months?: number;
  pets_allowed?: boolean;
  available_from?: string;
  created_at: string;
  updated_at: string;
}

export function useProperties() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { isAdmin } = useUserRoles();
  const { t } = useLanguage();

  const fetchProperties = async () => {
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .eq('approval_status', 'approved')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Sort boosted properties first (with valid boost)
      const sortedData = (data || []).sort((a, b) => {
        const aIsBoosted = a.is_boosted && a.boost_expires_at && new Date(a.boost_expires_at) > new Date();
        const bIsBoosted = b.is_boosted && b.boost_expires_at && new Date(b.boost_expires_at) > new Date();
        
        if (aIsBoosted && !bIsBoosted) return -1;
        if (!aIsBoosted && bIsBoosted) return 1;
        return 0;
      });
      
      setProperties(sortedData);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast({
        title: t('toast.error'),
        description: t('toast.properties_load_error'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addProperty = async (propertyData: Omit<Property, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user) {
      toast({
        title: t('toast.auth_required'),
        description: t('toast.sign_in_properties'),
        variant: "destructive",
      });
      return false;
    }

    try {
      const { data, error } = await supabase
        .from('properties')
        .insert([{ ...propertyData, user_id: user.id }])
        .select()
        .single();

      if (error) throw error;
      
      setProperties(prev => [data, ...prev]);
      toast({
        title: t('toast.success'),
        description: t('toast.property_added'),
      });

      // Send agency notification for property_added (non-blocking)
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('agency_id, display_name')
          .eq('user_id', user.id)
          .single();

        if (profile?.agency_id) {
          supabase.functions.invoke('send-agency-notification', {
            body: {
              agency_id: profile.agency_id,
              notification_type: 'property_added',
              broker_name: profile.display_name || 'A broker',
              property_address: data.address,
              property_city: data.city,
              property_id: data.id
            }
          }).catch(err => console.error('Agency notification failed:', err));
        }
      } catch (notifyError) {
        console.error('Error checking broker agency:', notifyError);
      }

      return true;
    } catch (error) {
      console.error('Error adding property:', error);
      toast({
        title: t('toast.error'),
        description: t('toast.property_add_error'),
        variant: "destructive",
      });
      return false;
    }
  };

  const updateProperty = async (id: string, updates: Partial<Property>) => {
    if (!user) return false;

    try {
      const baseQuery = supabase
        .from('properties')
        .update(updates)
        .eq('id', id);

      const query = isAdmin() ? baseQuery : baseQuery.eq('user_id', user.id);

      const { data, error } = await query
        .select()
        .single();

      if (error) throw error;
      
      setProperties(prev => prev.map(p => p.id === id ? data : p));
      toast({
        title: t('toast.success'),
        description: t('toast.property_updated'),
      });
      return true;
    } catch (error) {
      console.error('Error updating property:', error);
      toast({
        title: t('toast.error'),
        description: t('toast.property_update_error'),
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteProperty = async (id: string) => {
    if (!user) return false;

    try {
      const baseDelete = supabase
        .from('properties')
        .delete()
        .eq('id', id);

      const { error } = await (isAdmin() ? baseDelete : baseDelete.eq('user_id', user.id));

      if (error) throw error;
      
      setProperties(prev => prev.filter(p => p.id !== id));
      toast({
        title: t('toast.success'),
        description: t('toast.property_deleted'),
      });
      return true;
    } catch (error) {
      console.error('Error deleting property:', error);
      toast({
        title: t('toast.error'),
        description: t('toast.property_delete_error'),
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    // Always fetch properties on mount, regardless of auth state
    fetchProperties();

    // Set up real-time subscription
    const channel = supabase
      .channel('properties_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'properties'
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setProperties(prev => [payload.new as Property, ...prev]);
          } else if (payload.eventType === 'UPDATE') {
            setProperties(prev => prev.map(p => 
              p.id === payload.new.id ? payload.new as Property : p
            ));
          } else if (payload.eventType === 'DELETE') {
            setProperties(prev => prev.filter(p => p.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Refetch properties when auth state changes to ensure fresh data
  useEffect(() => {
    if (!loading) {
      fetchProperties();
    }
  }, [user?.id]); // Only depend on user.id, not the entire user object

  return {
    properties,
    loading,
    addProperty,
    updateProperty,
    deleteProperty,
    refetch: fetchProperties
  };
}